import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ReactSelect from "react-select";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

// === Styled components ===
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

// react-select light styling to blend in
const selectStyles = {
  container: (base) => ({ ...base, minWidth: 260 }),
  menu: (base) => ({ ...base, zIndex: 5 }),
  control: (base) => ({
    ...base,
    borderRadius: 8,
    minHeight: 40,
    borderColor: "#ccc",
    boxShadow: "none",
    ":hover": { borderColor: "#aaa" },
    backgroundColor: "#fff",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
  input: (base) => ({ ...base, margin: 0 }),
  placeholder: (base) => ({ ...base, color: "#777" }),
};

const months = [
  { value: 0, label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// === Component ===
const ContractorsComparisonChartZakupy = ({ data = [] }) => {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(0);

  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState({
    contractor1: "",
    contractor2: "",
    contractor3: "",
  });

  // Extract available years and set default to the latest
  useEffect(() => {
    if (!data.length) return;
    const extractedYears = [
      ...new Set(
        data.map((row) => {
          const d = new Date(row.DataWystawienia);
          const y = !isNaN(d) ? d.getFullYear() : parseInt(row.Rok, 10);
          return y || null;
        })
      ),
    ]
      .filter(Boolean)
      .sort((a, b) => a - b);

    setYears(extractedYears);
    if (!selectedYear && extractedYears.length) {
      setSelectedYear(String(extractedYears[extractedYears.length - 1]));
    }
  }, [data, selectedYear]);

  // Filter by year & month
  const filteredData = useMemo(() => {
    if (!data.length || !selectedYear) return [];
    return data.filter((row) => {
      const d = new Date(row.DataWystawienia);
      const y = !isNaN(d) ? d.getFullYear() : parseInt(row.Rok, 10);
      const m = !isNaN(d) ? d.getMonth() + 1 : parseInt(row.Miesiac, 10);
      if (String(y) !== String(selectedYear)) return false;
      if (selectedMonth !== 0 && m !== selectedMonth) return false;
      return true;
    });
  }, [data, selectedYear, selectedMonth]);

  // Build contractor list (sorted by total purchases in the filtered range)
  useEffect(() => {
    if (!filteredData.length) {
      setContractors([]);
      return;
    }
    const aggregated = filteredData.reduce((acc, row) => {
      const name = row.NazwaDostawcy?.trim() || "Nieznany dostawca";
      const value = (parseFloat(row.K_42) || 0) + (parseFloat(row.K_43) || 0);
      acc[name] = (acc[name] || 0) + value;
      return acc;
    }, {});
    const sorted = Object.entries(aggregated)
      .sort(([, a], [, b]) => b - a)
      .map(([name]) => name);

    setContractors(sorted);

    // Set defaults only if not yet chosen
    setSelectedContractors((prev) => ({
      contractor1: prev.contractor1 || sorted[0] || "",
      contractor2: prev.contractor2 || sorted[1] || "",
      contractor3: prev.contractor3 || sorted[2] || "",
    }));
  }, [filteredData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const selected = Object.values(selectedContractors).filter(Boolean);
    if (!selected.length || !filteredData.length) return null;

    const agg = filteredData.reduce((acc, row) => {
      const name = row.NazwaDostawcy?.trim() || "Nieznany dostawca";
      if (!selected.includes(name)) return acc;
      const value = (parseFloat(row.K_42) || 0) + (parseFloat(row.K_43) || 0);
      if (!acc[name]) acc[name] = { total: 0, count: 0 };
      acc[name].total += value;
      acc[name].count += 1;
      return acc;
    }, {});

    const labels = Object.keys(agg);
    const totals = labels.map((n) => agg[n].total);
    const counts = labels.map((n) => agg[n].count);
    const avgs = labels.map((n) =>
      agg[n].count > 0 ? agg[n].total / agg[n].count : 0
    );

    return {
      labels,
      datasets: [
        {
          label: "Całkowite zakupy",
          data: totals,
          backgroundColor: "#9848c7", // purchases theme
          yAxisID: "y",
          type: "bar",
          borderWidth: 1,
        },
        {
          label: "Liczba transakcji",
          data: counts,
          backgroundColor: "#4CAF50",
          yAxisID: "y1",
          type: "bar",
          borderWidth: 1,
        },
        {
          label: "Średnia wartość transakcji",
          data: avgs,
          type: "line",
          borderColor: "#ff9800",
          backgroundColor: "rgba(255, 152, 0, 0.2)",
          yAxisID: "y2",
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 3,
          fill: true,
        },
      ],
    };
  }, [filteredData, selectedContractors]);

  const contractorOptions = useMemo(
    () => contractors.map((c) => ({ value: c, label: c })),
    [contractors]
  );

  const selectedOption = (key) => {
    const val = selectedContractors[key];
    return val ? { value: val, label: val } : null;
    };

  const handleContractorChange = (key, option) => {
    setSelectedContractors((prev) => ({ ...prev, [key]: option?.value || "" }));
  };

  return (
    <ChartWrapper>
      <ChartTitle>
        {/* Updated title */}
        Porównanie zakupów oraz liczby transakcji dla dostawców
        <InfoIconWrapper data-tooltip-id="zakupy-compare-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="zakupy-compare-tooltip"
        place="right"
        content={
          <>
            <strong>Porównanie dostawców</strong><br />
            Filtruj po roku i miesiącu. Każda lista dostawców ma wyszukiwarkę wbudowaną w rozwijaną listę.<br />
            Fioletowe słupki – zakupy, zielone – liczba transakcji, pomarańczowa linia – średnia wartość.
          </>
        }
        style={{ fontSize: "12px", color: "#fff" }}
      />

      {/* Global filters */}
      <FilterBar>
        <select
          value={selectedYear || ""}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: "1px solid #ccc", background: "#fff" }}
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
          style={{ padding: 8, borderRadius: 8, border: "1px solid #ccc", background: "#fff" }}
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </FilterBar>

      {/* Contractor pickers with search */}
      <FilterBar>
        {["contractor1", "contractor2", "contractor3"].map((key, i) => (
          <ReactSelect
            key={key}
            styles={selectStyles}
            options={contractorOptions}
            value={selectedOption(key)}
            onChange={(opt) => handleContractorChange(key, opt)}
            isSearchable
            placeholder={`Wybierz dostawcę (${i + 1})`}
            noOptionsMessage={() => "Brak wyników"}
          />
        ))}
      </FilterBar>

      {chartData ? (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (context) => {
                    const { dataset, parsed } = context;
                    // Format integers for counts, currency-like for values
                    if (dataset.label.includes("Liczba")) {
                      return `${dataset.label}: ${parsed.y}`;
                    }
                    return `${dataset.label}: ${formatNumber(parsed.y)}`;
                  },
                },
              },
            },
            scales: {
              x: { title: { display: true, text: "Dostawcy" } },
              y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "Zakupy" },
                ticks: { callback: (v) => formatNumber(v) },
              },
              y1: {
                type: "linear",
                position: "right",
                grid: { drawOnChartArea: false },
                title: { display: true, text: "Transakcje" },
                ticks: { callback: (v) => `${v}` },
              },
              y2: {
                type: "linear",
                position: "right",
                offset: true,
                grid: { drawOnChartArea: false },
                title: { display: true, text: "Średnia wartość" },
                ticks: { callback: (v) => formatNumber(v) },
              },
            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>
          Brak danych dla wybranych filtrów lub wybierz dostawców z listy.
        </p>
      )}
    </ChartWrapper>
  );
};

export default ContractorsComparisonChartZakupy;
